import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ChevronRight, ChevronDown, Package, FileText, Mail, Tag, BookOpen,
  Archive, FolderOpen, Folder, Clipboard, Pen, Pencil, Scissors, Paperclip,
  Droplet, Calculator, Printer, Briefcase, GraduationCap, Palette, Sparkles,
  Heart, Monitor, Zap, Box, Inbox, LayoutGrid, Frame, Megaphone, Gift,
  ShoppingBag, Calendar, BookMarked, Badge, UserCircle, Layers, Menu, X,
  Presentation
} from "lucide-react";
import { API } from "@/lib/api";

// Icon mapping
const ICONS = {
  FileText, Mail, Tag, BookOpen, Archive, FolderOpen, Folder,
  Clipboard, Pen, Pencil, Scissors, Paperclip, Droplet, Calculator,
  Printer, Briefcase, GraduationCap, Palette, Sparkles, Heart, Monitor, Zap, Box,
  Inbox, LayoutGrid, Frame, Megaphone, Gift, ShoppingBag, Calendar,
  BookMarked, Badge, UserCircle, Layers, Package, Presentation
};

function getIcon(iconName) {
  return ICONS[iconName] || Package;
}

// Clean category name (remove leading numbers)
function cleanName(name) {
  if (!name) return '';
  return name.replace(/^\d+\./, '').trim();
}

// Readable short label for category bar
function menuLabel(name) {
  const clean = cleanName(name);
  // Custom short labels for known long names
  const shortMap = {
    'PAPÍR': 'Papír a obálky',
    'ARCHIVACE': 'Archivace',
    'PSACÍ': 'Psací potřeby',
    'DROBNÉ': 'Drobné kancel.',
    'KOREKČNÍ': 'Korekce a lepení',
    'KANCELÁŘSKÉ DOPLŇKY': 'Kancel. doplňky',
    'DATOVÁ': 'Datová média',
    'PREZENTACE': 'Prezentace',
    'ELEKTRO': 'Elektro',
    'OBALOVÝ': 'Obaly',
    'DROGERIE': 'Drogerie',
  };
  for (const [key, label] of Object.entries(shortMap)) {
    if (clean.startsWith(key)) return label;
  }
  const short = clean.split(',')[0].trim();
  if (short.length > 20) return short.split(/\s+/).slice(0, 2).join(' ');
  return short;
}

export function MegaMenu() {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(res => {
        const topLevel = res.data.filter(c => c.level_depth === 1 && c.name);
        setCategories(topLevel);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCat = categories.find(c => c.id_category === activeCategory);

  return (
    <>
      {/* Desktop Menu */}
      <nav className="hidden lg:block bg-slate-50 border-t border-b border-slate-200" ref={menuRef}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-0">
            {/* All categories button */}
            <div className="relative flex-shrink-0">
              <button
                className={`flex items-center gap-2 px-5 py-3.5 font-semibold text-sm transition-colors rounded-sm ${activeCategory ? 'bg-[#004E98] text-white' : 'bg-[#004E98]/10 text-[#004E98] hover:bg-[#004E98]/20'}`}
                onMouseEnter={() => setActiveCategory('all')}
                data-testid="all-categories-btn"
              >
                <Menu className="w-5 h-5" />
                Všechny kategorie
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Dropdown */}
              {activeCategory && (
                <div 
                  className="absolute left-0 top-full bg-white border border-slate-200 shadow-2xl z-50 w-[900px] max-h-[70vh] overflow-hidden rounded-b-md"
                  onMouseLeave={() => setActiveCategory(null)}
                >
                  <div className="flex">
                    {/* Categories list */}
                    <div className="w-72 bg-slate-50 border-r border-slate-200 max-h-[70vh] overflow-y-auto">
                      {categories.map(cat => {
                        const IconComponent = getIcon(cat.icon);
                        return (
                          <div
                            key={cat.id_category}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-l-3 ${activeCategory === cat.id_category ? 'bg-white text-[#004E98] border-l-[3px] border-l-[#004E98]' : 'border-l-[3px] border-l-transparent hover:bg-white'}`}
                            onMouseEnter={() => setActiveCategory(cat.id_category)}
                          >
                            <IconComponent className="w-5 h-5 text-[#004E98] flex-shrink-0" />
                            <span className="flex-1 text-sm font-medium leading-tight">{cleanName(cat.name)}</span>
                            {cat.children && cat.children.length > 0 && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Subcategories panel */}
                    <div className="flex-1 p-6 max-h-[70vh] overflow-y-auto">
                      {selectedCat ? (
                        <div>
                          <Link
                            to={`/kategorie/${selectedCat.id_category}`}
                            className="flex items-center gap-2 text-lg font-bold text-[#004E98] mb-5 hover:underline"
                            onClick={() => setActiveCategory(null)}
                          >
                            {React.createElement(getIcon(selectedCat.icon), { className: "w-6 h-6" })}
                            {cleanName(selectedCat.name)}
                          </Link>
                          
                          {selectedCat.children && selectedCat.children.length > 0 ? (
                            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                              {selectedCat.children.map(sub => (
                                <div key={sub.id_category}>
                                  <Link
                                    to={`/kategorie/${sub.id_category}`}
                                    className="flex items-center gap-2 font-semibold text-slate-800 hover:text-[#004E98] mb-2 text-sm"
                                    onClick={() => setActiveCategory(null)}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] flex-shrink-0"></span>
                                    {cleanName(sub.name)}
                                  </Link>
                                  {sub.children && sub.children.length > 0 && (
                                    <ul className="ml-4 space-y-1">
                                      {sub.children.slice(0, 5).map(child => (
                                        <li key={child.id_category}>
                                          <Link
                                            to={`/kategorie/${child.id_category}`}
                                            className="text-sm text-slate-500 hover:text-[#004E98] transition-colors"
                                            onClick={() => setActiveCategory(null)}
                                          >
                                            {cleanName(child.name)}
                                          </Link>
                                        </li>
                                      ))}
                                      {sub.children.length > 5 && (
                                        <li>
                                          <Link to={`/kategorie/${sub.id_category}`} className="text-xs font-medium text-[#004E98] hover:underline" onClick={() => setActiveCategory(null)}>
                                            + {sub.children.length - 5} dalších
                                          </Link>
                                        </li>
                                      )}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Link to={`/kategorie/${selectedCat.id_category}`} className="inline-flex items-center gap-2 text-[#004E98] hover:underline font-medium" onClick={() => setActiveCategory(null)}>
                              Zobrazit všechny produkty <ChevronRight className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          {categories.map(cat => (
                            <Link
                              key={cat.id_category}
                              to={`/kategorie/${cat.id_category}`}
                              className="flex items-center gap-3 p-3 bg-slate-50 rounded-md hover:bg-[#004E98]/5 hover:border-[#004E98]/20 border border-transparent transition-colors"
                              onClick={() => setActiveCategory(null)}
                            >
                              <div className="w-10 h-10 bg-[#004E98]/10 rounded-md flex items-center justify-center flex-shrink-0">
                                {React.createElement(getIcon(cat.icon), { className: "w-5 h-5 text-[#004E98]" })}
                              </div>
                              <div>
                                <div className="font-semibold text-slate-800 text-sm leading-tight">{cleanName(cat.name)}</div>
                                {cat.children?.length > 0 && (
                                  <div className="text-xs text-slate-400 mt-0.5">{cat.children.length} podkategorií</div>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Category icons row - centered */}
            <div className="flex items-center justify-center flex-1 gap-0">
              {categories.slice(0, 8).map(cat => {
                const IconComponent = getIcon(cat.icon);
                return (
                  <Link
                    key={cat.id_category}
                    to={`/kategorie/${cat.id_category}`}
                    className="flex flex-col items-center gap-1.5 px-4 py-2.5 text-slate-600 hover:text-[#004E98] hover:bg-white transition-all rounded group"
                    title={cleanName(cat.name)}
                    data-testid={`menu-category-${cat.id_category}`}
                  >
                    <IconComponent className="w-7 h-7 group-hover:scale-110 transition-transform text-[#004E98]/70 group-hover:text-[#004E98]" />
                    <span className="text-[11px] font-semibold text-center leading-tight whitespace-nowrap">{menuLabel(cat.name)}</span>
                  </Link>
                );
              })}
              <Link
                to="/produkty"
                className="flex flex-col items-center gap-1.5 px-4 py-2.5 text-[#FF6B35] hover:text-[#E85D2A] hover:bg-white transition-all rounded"
                data-testid="menu-all-products"
              >
                <Package className="w-7 h-7" />
                <span className="text-[11px] font-semibold">Vše</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile & Tablet Menu */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200"
        >
          <span className="flex items-center gap-2 font-medium text-slate-700">
            <Menu className="w-5 h-5" />
            Kategorie
          </span>
          {mobileOpen ? <X className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {mobileOpen && (
          <div className="bg-white border-t border-slate-200 max-h-[60vh] overflow-y-auto">
            {categories.map(cat => {
              const IconComponent = getIcon(cat.icon);
              const isExpanded = mobileExpanded === cat.id_category;
              const hasChildren = cat.children && cat.children.length > 0;
              
              return (
                <div key={cat.id_category}>
                  <div className="flex items-center border-b border-slate-100">
                    <Link
                      to={`/kategorie/${cat.id_category}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 flex items-center gap-3 px-4 py-3 font-medium"
                    >
                      <IconComponent className="w-4 h-4 text-[#004E98]" />
                      {cleanName(cat.name)}
                    </Link>
                    {hasChildren && (
                      <button
                        onClick={() => setMobileExpanded(isExpanded ? null : cat.id_category)}
                        className="p-3"
                      >
                        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                    )}
                  </div>
                  {isExpanded && hasChildren && (
                    <div className="bg-slate-50 pl-6">
                      {cat.children.slice(0, 8).map(sub => (
                        <Link
                          key={sub.id_category}
                          to={`/kategorie/${sub.id_category}`}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-2 text-sm text-slate-600 border-b border-slate-100"
                        >
                          {cleanName(sub.name)}
                        </Link>
                      ))}
                      {cat.children.length > 8 && (
                        <Link
                          to={`/kategorie/${cat.id_category}`}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-2 text-sm text-[#004E98] font-medium"
                        >
                          + dalších {cat.children.length - 8}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/produkty"
              onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 font-medium text-[#FF6B35] border-t border-slate-200"
            >
              Všechny produkty
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
